import { useDaily, useDailyEvent, useMeetingState } from '@daily-co/daily-react'
import { useAutoUpdatingRef } from '@quickmd-llc/frontend-utils'
import type { MutableRefObject } from 'react'
import { useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { setVisitInfo } from 'store/waitingRoom/WaitingRoomActions'
import type { OnStartVisitClickInVisit } from '../components/DailyVisitProvider'
import { noop } from 'lodash'
import { useDeleteVisitsVisitIdVideo } from 'api/generated/visit/visit'
import { useSelector } from 'store/hooks/useSelector'
import { useDailyVisitInfo } from './useDailyVisitInfo'
import { playDialOutDisconnectTone } from '../playDialOutTone'

type Input = {
  onStartVisitClickInVisit: OnStartVisitClickInVisit
  /** Optional ref to a function (e.g. save notes) to run before ending the visit call. */
  beforeEndVisitCallRef?: MutableRefObject<(() => Promise<void>) | null | undefined>
}

export interface DailyDisconnectMethods {
  /**
   * Always `await` this disconnect method (see `useDailyDisconnectMethods` docs).
   */
  endVisitCall: () => Promise<void>

  /**
   * Always `await` this disconnect method (see `useDailyDisconnectMethods` docs).
   */
  retryMakingConnection: (args: { isPhoneCall: boolean; onEndVisitComplete: () => void }) => Promise<void>

  /**
   * Always `await` this disconnect method (see `useDailyDisconnectMethods` docs).
   */
  resetDailyVisit: () => Promise<void>
}

export type UseDailyDisconnectMethodsReturn = DailyDisconnectMethods
/**
 * !!VERY IMPORTANT TO ALWAYS AWAIT ANY DISCONNECT METHODS PRIOR TO CONTINUING ON WITH ANY ADDITIONAL CALL RELATED CODE.
 * IF THE CALL OBJECT IS NOT DESTROYED PRIOR TO ATTEMPTING OTHER/NEW CALL FUNCTIONS, INTERMITTENT AND DIFFICULT TO DEBUG ERRORS WILL OCCUR.
 */
export function useDailyDisconnectMethods({
  onStartVisitClickInVisit,
  beforeEndVisitCallRef,
}: Input): DailyDisconnectMethods {
  const onStartVisitClickInVisitRef = useAutoUpdatingRef(onStartVisitClickInVisit)

  const callSessionIdRef = useRef<string | null>(null)

  // if making a phone call, we need to store the session id so we can communicate the call needs to be ended, which
  // does not automatically happen when destroying the call instance
  useDailyEvent('dialout-connected', (e) => {
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    callSessionIdRef.current = e.sessionId
  })
  useDailyEvent('dialout-stopped', () => {
    callSessionIdRef.current = null
    playDialOutDisconnectTone()
  })
  useDailyEvent('dialout-error', () => {
    callSessionIdRef.current = null
  })

  const callObject = useDaily()
  const meetingState = useMeetingState()
  const meetingStateRef = useAutoUpdatingRef(meetingState)
  const callObjectRef = useAutoUpdatingRef(callObject)
  const dispatch = useDispatch()
  const visitId = useSelector(
    (state) => state.waitingRoomReducer.visitInfo?.VisitId || state.waitingRoomReducer.activeVisitId,
  )

  const isVideoCall = !!useDailyVisitInfo()?.isVideoCall

  const { mutateAsync: deleteVisitVideo } = useDeleteVisitsVisitIdVideo()
  const endVisitCall = useCallback(async () => {
    await beforeEndVisitCallRef?.current?.().catch(noop)

    try {
      if (visitId && isVideoCall) await deleteVisitVideo({ visitId })
    } catch {
      console.warn('Failed to delete visit video on call end, proceeding with call disconnect anyway')
    }

    if (callSessionIdRef.current && meetingStateRef.current === 'joined-meeting') {
      try {
        await callObjectRef.current?.stopDialOut({ sessionId: callSessionIdRef.current })
      } catch {
        console.warn('Failed to stop dial out, proceeding with call disconnect anyway')
      }
      callSessionIdRef.current = null
    }
    try {
      callObjectRef.current?.setLocalVideo(false)
      callObjectRef.current?.setLocalAudio(false)
    } catch {
      console.warn('Failed to turn off local audio/video, proceeding with call disconnect anyway')
    }
    try {
      await callObjectRef.current?.leave()
    } catch {
      console.warn('Failed to leave daily call, proceeding with cleanup anyway')
    }
    callObjectRef.current = null
  }, [beforeEndVisitCallRef, callObjectRef, deleteVisitVideo, visitId, isVideoCall, meetingStateRef])

  const retryMakingConnection = useCallback(
    async ({
      isPhoneCall,
      onEndVisitComplete,
    }: {
      isPhoneCall: boolean
      onEndVisitComplete: () => void | Promise<void>
    }) => {
      await endVisitCall()
      // important to await the actual ending of the phone call before running any related callbacks and prior to starting a new call
      await onEndVisitComplete()
      onStartVisitClickInVisitRef.current({ isPhoneCall })
    },
    [endVisitCall, onStartVisitClickInVisitRef],
  )

  // Reset daily visit function
  const resetDailyVisit = useCallback(async () => {
    await endVisitCall()
    dispatch(setVisitInfo(null))
  }, [endVisitCall, dispatch])

  return useMemo(
    () => ({ resetDailyVisit, endVisitCall, retryMakingConnection }),
    [resetDailyVisit, endVisitCall, retryMakingConnection],
  )
}
